import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../config/api';
import { Post, Comment } from '../../types/comments';

interface AnalyticsTimeRange {
  startDate: string;
  endDate: string;
}

interface FeedbackState {
  posts: Post[];
  selectedPost?: Post;
  comments: Comment[];
  isLoading: boolean;
  error: string | null;
  filters: {
    owner_username?: string;
    post_type?: string;
  };
  pagination: {
    page: number;
    limit: number;
    totalItems: number;
    totalPages: number;
  };
  analytics: {
    postTypeDistribution: Array<{ type: string; count: number }>;
    engagementOverTime: Array<{
      date: string;
      likes: number;
      comments: number;
      views: number;
    }>;
    topEngagingPosts: Post[];
  };
}

interface GetPostsParams {
  filters?: FeedbackState['filters'];
  page?: number;
  limit?: number;
}

const initialState: FeedbackState = {
  posts: [],
  comments: [],
  isLoading: false,
  error: null,
  filters: {},
  pagination: {
    page: 1,
    limit: 10,
    totalItems: 0,
    totalPages: 0,
  },
  analytics: {
    postTypeDistribution: [],
    engagementOverTime: [],
    topEngagingPosts: [],
  },
};

export const getPosts = createAsyncThunk(
  'feedback/getPosts',
  async (params: any) => {
    let queryParams: any = {};
    
    if (params && typeof params === 'object') {
      if ('filters' in params) {
        // New format with pagination
        const { filters = {}, page = 1, limit = 10 } = params;
        queryParams = { ...filters, page, limit };
      } else {
        // Old format (just filters)
        queryParams = { ...params, page: 1, limit: 10 };
      }
    }
    
    const response = await api.get('/posts', { params: queryParams });
    return response.data;
  }
);

export const getPostById = createAsyncThunk(
  'feedback/getPostById',
  async (id: number) => {
    const response = await api.get(`/posts/${id}`);
    return response.data;
  }
);

export const getComments = createAsyncThunk(
  'feedback/getComments',
  async (postId: number) => {
    const response = await api.get(`/comments/post/${postId}`);
    return response.data;
  }
);

export const createComment = createAsyncThunk(
  'feedback/createComment',
  async (comment: Partial<Comment>) => {
    const response = await api.post('/comments', comment);
    return response.data;
  }
);

export const getPostTypeDistribution = createAsyncThunk(
  'feedback/getPostTypeDistribution',
  async (timeRange: AnalyticsTimeRange) => {
    const response = await api.get('/analytics/post-types', { params: timeRange });
    return response.data;
  }
);

export const getEngagementOverTime = createAsyncThunk(
  'feedback/getEngagementOverTime',
  async (timeRange: AnalyticsTimeRange) => {
    const response = await api.get('/analytics/engagement', { params: timeRange });
    return response.data;
  }
);

export const getTopEngagingPosts = createAsyncThunk(
  'feedback/getTopEngagingPosts',
  async (timeRange: AnalyticsTimeRange) => {
    const response = await api.get('/analytics/top-posts', { params: timeRange });
    return response.data;
  }
);

const feedbackSlice = createSlice({
  name: 'feedback',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = action.payload;
      state.pagination.page = 1;
    },
    clearFilters: (state) => {
      state.filters = {};
      state.pagination.page = 1;
    },
    setPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setLimit: (state, action) => {
      state.pagination.limit = action.payload;
      state.pagination.page = 1;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get Posts
      .addCase(getPosts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPosts.fulfilled, (state, action) => {
        state.isLoading = false;
        // Handle the case where the response might not have the expected structure
        if (action.payload && typeof action.payload === 'object') {
          if ('items' in action.payload) {
            // New API format with pagination
            state.posts = action.payload.items || [];
            state.pagination.totalItems = action.payload.totalItems || 0;
            state.pagination.totalPages = action.payload.totalPages || 1;
          } else if ('data' in action.payload) {
            // Current API format with data, total, page, limit
            state.posts = action.payload.data || [];
            state.pagination.totalItems = action.payload.total || 0;
            // Calculate totalPages based on total items and limit
            const limit = action.payload.limit || state.pagination.limit;
            const total = action.payload.total || 0;
            state.pagination.totalPages = Math.ceil(total / limit);
          } else if (Array.isArray(action.payload)) {
            // Old API format (just an array of posts)
            state.posts = action.payload;
            state.pagination.totalItems = action.payload.length;
            state.pagination.totalPages = 1;
          } else {
            // Unexpected format
            state.posts = [];
            state.pagination.totalItems = 0;
            state.pagination.totalPages = 0;
          }
        } else {
          // Empty or invalid response
          state.posts = [];
          state.pagination.totalItems = 0;
          state.pagination.totalPages = 0;
        }
      })
      .addCase(getPosts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch posts';
      })
      // Get Post by ID
      .addCase(getPostById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPostById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedPost = action.payload;
      })
      .addCase(getPostById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch post';
      })
      // Get Comments
      .addCase(getComments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getComments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.comments = action.payload;
      })
      .addCase(getComments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch comments';
      })
      // Create Comment
      .addCase(createComment.fulfilled, (state, action) => {
        state.comments.unshift(action.payload);
      })
      // Analytics cases
      .addCase(getPostTypeDistribution.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPostTypeDistribution.fulfilled, (state, action) => {
        state.isLoading = false;
        state.analytics.postTypeDistribution = action.payload;
      })
      .addCase(getPostTypeDistribution.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch post type distribution';
      })
      .addCase(getEngagementOverTime.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getEngagementOverTime.fulfilled, (state, action) => {
        state.isLoading = false;
        state.analytics.engagementOverTime = action.payload;
      })
      .addCase(getEngagementOverTime.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch engagement data';
      })
      .addCase(getTopEngagingPosts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getTopEngagingPosts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.analytics.topEngagingPosts = action.payload;
      })
      .addCase(getTopEngagingPosts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch top engaging posts';
      });
  },
});

export const { setFilters, clearFilters, setPage, setLimit } = feedbackSlice.actions;
export default feedbackSlice.reducer; 